/* eslint
    no-unused-expressions: "warn"
*/

import { globalEvent } from '@shared/easyfy.core';
import { invoke } from '@shared/easyfy.decorators';

namespace Easyfy.Modules {
  interface SiteMessage {
    text: string;
    linkUrl?: string | null;
    linkText?: string | null;
    severity?: string;
  }

  @invoke
  class SiteMessageRotator {
    private readonly root: HTMLElement;
    private readonly viewport: HTMLElement;
    private readonly pauseButton: HTMLElement | null;
    private currentItem: HTMLElement;
    private readonly messages: SiteMessage[];
    private readonly intervalMs: number;

    private currentIndex = 0;
    private timerId: number | null = null;
    private isPaused = false;
    private isTransitioning = false;
    private readonly fadeDurationMs = 300;

    onInit() {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      initSiteMessageRotators();
    }

    constructor(root: HTMLElement) {
      this.root = root;

      const viewport = root.querySelector<HTMLElement>(
        '.site-message-rotator__viewport'
      );
      const item = root.querySelector<HTMLElement>(
        '.site-message-rotator__item'
      );

      if (!viewport || !item) {
        throw new Error('Missing required rotator markup.');
      }

      this.viewport = viewport;
      this.currentItem = item;
      this.messages = this.readMessages();
      this.intervalMs = this.parseNumber(root.dataset.intervalMs, 5000);
      this.pauseButton = root.querySelector<HTMLElement>(
        '.site-message-rotator__pause'
      );
    }

    public init(): void {
      if (this.messages.length === 0) {
        this.root.style.display = 'none';
        return;
      }

      // Pre-render all messages so the tallest one always dictates the container height.
      this.renderInto(this.currentItem, this.messages[0]);
      this.currentItem.classList.add('is-active');

      for (let i = 1; i < this.messages.length; i++) {
        const item = document.createElement('div');
        item.className = 'site-message-rotator__item';
        this.renderInto(item, this.messages[i]);
        this.viewport.appendChild(item);
      }

      if (this.messages.length > 1) {
        this.pauseButton?.addEventListener('click', this.onPauseClick);
        this.start();
      }
    }

    private readMessages(): SiteMessage[] {
      const script = this.root.querySelector<HTMLScriptElement>(
        '.site-message-rotator__data'
      );
      if (!script?.textContent) {
        return [];
      }

      try {
        const json = script.textContent.trim();
        const parsed = JSON.parse(json) as SiteMessage[];
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        console.error('Invalid site message JSON.');
        return [];
      }
    }

    private parseNumber(value: string | undefined, fallback: number): number {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    }

    private start(): void {
      this.stop();

      this.timerId = window.setInterval(() => {
        if (this.isPaused || this.isTransitioning) {
          return;
        }

        this.showNext();
      }, this.intervalMs);
    }

    private stop(): void {
      if (this.timerId !== null) {
        window.clearInterval(this.timerId);
        this.timerId = null;
      }
    }

    private showNext(): void {
      const items = Array.from(
        this.viewport.querySelectorAll<HTMLElement>(
          '.site-message-rotator__item'
        )
      );
      const nextIndex = (this.currentIndex + 1) % this.messages.length;
      const nextItem = items[nextIndex];

      if (!nextItem) return;

      this.isTransitioning = true;

      this.currentItem.classList.remove('is-active');
      this.currentItem.classList.add('is-leaving');
      nextItem.classList.add('is-active');

      const previous = this.currentItem;
      this.currentItem = nextItem;
      this.currentIndex = nextIndex;

      window.setTimeout(() => {
        previous.classList.remove('is-leaving');
        this.isTransitioning = false;
      }, this.fadeDurationMs);
    }

    private renderInto(container: HTMLElement, message: SiteMessage): void {
      container.innerHTML = '';

      const wrapper = document.createElement(message.linkUrl ? 'a' : 'div');
      wrapper.className = 'site-message-rotator__content';

      if (message.linkUrl && wrapper instanceof HTMLAnchorElement) {
        wrapper.href = message.linkUrl;
      }

      if (message.severity) {
        wrapper.dataset.severity = message.severity.toLowerCase();
        container.dataset.severity = message.severity.toLowerCase();
      }

      const text = document.createElement('span');
      text.className = 'site-message-rotator__text';
      text.textContent = message.text;
      wrapper.appendChild(text);

      if (message.linkUrl && message.linkText) {
        const linkText = document.createElement('span');
        linkText.className = 'site-message-rotator__link-text';
        linkText.textContent = ` ${message.linkText}`;
        wrapper.appendChild(linkText);
      }

      container.appendChild(wrapper);
    }

    private readonly onPauseClick = (): void => {
      this.isPaused = !this.isPaused;
      this.pauseButton?.setAttribute('aria-pressed', String(this.isPaused));
    };
  }

  export function initSiteMessageRotators(): void {
    const roots = document.querySelectorAll<HTMLElement>(
      '[data-site-message-rotator]'
    );

    roots.forEach((root) => {
      const rotator = new SiteMessageRotator(root);
      rotator.init();
    });
  }
}
